import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { CompanyService } from "services/companyService";
import { GeneralJournalService } from "services/general-journal";
import { formatDate, formatVndIntegerValue } from "utils";
import toast from "react-hot-toast";

const defaultYear = String(new Date().getFullYear());

const ui = {
  title: { fontSize: 20, lineHeight: 1.2 },
  table: { fontSize: 13 },
  th: { border: "1px solid #000", padding: 6, textAlign: "center", fontSize: 13 },
  tdLeft: { border: "1px solid #000", padding: 6, textAlign: "left", fontSize: 13 },
  tdCenter: { border: "1px solid #000", padding: 6, textAlign: "center", fontSize: 13 },
  tdRight: { border: "1px solid #000", padding: 6, textAlign: "right", fontSize: 13 },
};

const formatMoney = (value) => formatVndIntegerValue(value || 0);

const getPrintTemplate = ({ unit, companyAddress, filters, items, summary }) => {
  const bodyRows = items
    .map(
      (item) => `
      <tr>
        <td class="cell center">${formatDate(item.recordDate)}</td>
        <td class="cell center">${item.voucherNumber || ""}</td>
        <td class="cell center">${formatDate(item.voucherDate)}</td>
        <td class="cell left">${item.description || ""}</td>
        <td class="cell center">${item.postedToLedger ? "x" : ""}</td>
        <td class="cell center">${item.lineNumber || ""}</td>
        <td class="cell center">${item.accountCode || ""}</td>
        <td class="cell right">${item.debit ? formatMoney(item.debit) : ""}</td>
        <td class="cell right">${item.credit ? formatMoney(item.credit) : ""}</td>
      </tr>`
    )
    .join("");

  return `
  <html>
    <head>
      <meta charset="UTF-8" />
      <title>Sổ Nhật Ký Chung S03a-DN</title>
      <style>
        body { font-family: 'Times New Roman', serif; color: #000; margin: 24px; font-size: 14px; }
        .header { display: flex; justify-content: space-between; margin-bottom: 12px; }
        .cell, .head { border: 1px solid #000; padding: 6px; vertical-align: middle; }
        .head { font-weight: 700; text-align: center; }
        table { width: 100%; border-collapse: collapse; }
        .title { text-align: center; margin: 10px 0 16px; }
        .title h1 { margin: 0; font-size: 26px; text-transform: uppercase; }
        .title p { margin: 4px 0; }
        .center { text-align: center; }
        .left { text-align: left; }
        .right { text-align: right; }
        .sign { margin-top: 48px; display: flex; justify-content: space-between; align-items: flex-start; text-align: center; }
        .sign-col { width: 32%; }
        .sign-date-row { min-height: 20px; margin-bottom: 2px; }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <div><strong>Đơn vị:</strong> ${unit || "......................................"}</div>
          <div><strong>Địa chỉ:</strong> ${companyAddress || "......................................"}</div>
        </div>
        <div class="center">
          <div><strong>Mẫu số S03a-DN</strong></div>
          <div><em>(Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</em></div>
        </div>
      </div>
      <div class="title">
        <h1>Sổ Nhật Ký Chung</h1>
        <p>(Dùng cho hình thức kế toán Nhật ký chung)</p>
        <p>Năm: ${filters.year}</p>
      </div>
      <table>
        <thead>
          <tr>
            <th class="head">Ngày, tháng ghi sổ<br/>A</th>
            <th class="head">Số hiệu<br/>B</th>
            <th class="head">Ngày, tháng<br/>C</th>
            <th class="head">Diễn giải<br/>D</th>
            <th class="head">Sổ Cái<br/>E</th>
            <th class="head">Số dòng<br/>G</th>
            <th class="head">Tài khoản<br/>H</th>
            <th class="head">Nợ<br/>1</th>
            <th class="head">Có<br/>2</th>
          </tr>
        </thead>
        <tbody>
          ${bodyRows || '<tr><td class="cell center" colspan="9">Không có dữ liệu</td></tr>'}
          <tr>
            <td class="cell left" colspan="7"><strong>Cộng số phát sinh</strong></td>
            <td class="cell right"><strong>${formatMoney(summary.totalDebit || 0)}</strong></td>
            <td class="cell right"><strong>${formatMoney(summary.totalCredit || 0)}</strong></td>
          </tr>
        </tbody>
      </table>
      <div class="sign">
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Người ghi sổ</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Kế toán trưởng</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row"><em>Ngày ...... tháng ...... năm ......</em></div>
          <div><strong>Người đại diện theo pháp luật</strong></div>
          <div><em>(Ký, họ tên, đóng dấu)</em></div>
        </div>
      </div>
    </body>
  </html>`;
};

export default function JournalEntriesDetail({ baseRoute = "/templates/journal-entries" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [unit, setUnit] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [filters, setFilters] = useState({ year: defaultYear, fromDate: "", toDate: "", searchBy: "" });
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ totalRows: 0, totalDebit: 0, totalCredit: 0, postedToLedgerCount: 0 });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || "");
    setFilters({
      year: searchParams.get("year") || defaultYear,
      fromDate: searchParams.get("fromDate") || "",
      toDate: searchParams.get("toDate") || "",
      searchBy: searchParams.get("searchBy") || "",
    });
  }, [location.search]);

  useEffect(() => {
    const init = async () => {
      const response = await CompanyService.getByAuth();
      const payload = response?.data || response || {};
      setCompanyId(payload?._id || "");
      setUnit(payload?.name || payload?.companyName || payload?.unit || "");
      setCompanyAddress(payload?.address || payload?.companyAddress || "");
    };
    init();
  }, []);

  const fetchData = useCallback(async () => {
    if (!companyId || !filters.year) return;
    try {
      setLoading(true);
      const response = await GeneralJournalService.getJournalBook({
        company: companyId,
        year: Number(filters.year),
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        searchBy: filters.searchBy || undefined,
        paginate: false,
      });
      const payload = response?.data || response || {};
      setItems(payload.items || []);
      setSummary(payload.summary || { totalRows: 0, totalDebit: 0, totalCredit: 0, postedToLedgerCount: 0 });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải chi tiết Sổ nhật ký chung");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrint = () => {
    const html = getPrintTemplate({ unit, companyAddress, filters, items, summary });
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Trình duyệt đang chặn cửa sổ in. Vui lòng cho phép pop-up.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 150);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={11}>
            <Card>
              <SoftBox p={3} display="flex" flexDirection="column" minHeight="calc(100vh - 220px)">
                <SoftBox mb={2}>
                  <SoftTypography variant="h5" fontWeight="bold" sx={ui.title}>
                    Sổ Nhật Ký Chung (S03a-DN)
                  </SoftTypography>
                </SoftBox>
                <SoftBox border="1px solid #000" p={3} sx={{ fontFamily: "'Times New Roman', serif", color: "#000" }}>
                  <SoftBox display="flex" justifyContent="space-between" mb={2}>
                    <SoftBox>
                      <div style={{ fontSize: 14 }}><strong>Đơn vị:</strong> {unit || "......................................"}</div>
                      <div style={{ fontSize: 14 }}><strong>Địa chỉ:</strong> {companyAddress || "......................................"}</div>
                    </SoftBox>
                    <SoftBox textAlign="center" width="48%">
                      <div style={{ fontSize: 14, fontWeight: 700 }}>Mẫu số S03a-DN</div>
                      <div style={{ fontSize: 14, fontStyle: "italic" }}>
                        (Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                      </div>
                    </SoftBox>
                  </SoftBox>
                  <SoftBox textAlign="center" mb={2}>
                    <div style={{ fontSize: 28, fontWeight: 700, textTransform: "uppercase" }}>Sổ Nhật Ký Chung</div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>(Dùng cho hình thức kế toán Nhật ký chung)</div>
                    <div style={{ fontSize: 14, marginTop: 6 }}>Năm: {filters.year}</div>
                  </SoftBox>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100, ...ui.table }}>
                      <thead>
                        <tr>
                          <th style={ui.th}>Ngày, tháng ghi sổ<br />A</th>
                          <th style={ui.th}>Số hiệu<br />B</th>
                          <th style={ui.th}>Ngày, tháng<br />C</th>
                          <th style={ui.th}>Diễn giải<br />D</th>
                          <th style={ui.th}>Sổ Cái<br />E</th>
                          <th style={ui.th}>Số dòng<br />G</th>
                          <th style={ui.th}>Tài khoản<br />H</th>
                          <th style={ui.th}>Nợ<br />1</th>
                          <th style={ui.th}>Có<br />2</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && (
                          <tr>
                            <td style={ui.tdCenter} colSpan={9}>Đang tải dữ liệu...</td>
                          </tr>
                        )}
                        {!loading && !items.length && (
                          <tr>
                            <td style={ui.tdCenter} colSpan={9}>Không có dữ liệu</td>
                          </tr>
                        )}
                        {!loading && items.map((item, index) => (
                          <tr key={`${item.voucherNumber || 'row'}-${item.lineNumber || index}-${index}`}>
                            <td style={ui.tdCenter}>{formatDate(item.recordDate)}</td>
                            <td style={ui.tdCenter}>{item.voucherNumber || ""}</td>
                            <td style={ui.tdCenter}>{formatDate(item.voucherDate)}</td>
                            <td style={ui.tdLeft}>{item.description || ""}</td>
                            <td style={ui.tdCenter}>{item.postedToLedger ? "x" : ""}</td>
                            <td style={ui.tdCenter}>{item.lineNumber || ""}</td>
                            <td style={ui.tdCenter}>{item.accountCode || ""}</td>
                            <td style={ui.tdRight}>{item.debit ? formatMoney(item.debit) : ""}</td>
                            <td style={ui.tdRight}>{item.credit ? formatMoney(item.credit) : ""}</td>
                          </tr>
                        ))}
                        <tr>
                          <td style={{ ...ui.tdLeft, fontWeight: 700 }} colSpan={7}>Cộng số phát sinh</td>
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>{formatMoney(summary.totalDebit || 0)}</td>
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>{formatMoney(summary.totalCredit || 0)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <SoftBox mt={2} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                    <SoftTypography variant="caption" color="text">
                      Tổng số dòng: {summary.totalRows} - Đã đánh dấu Sổ Cái: {summary.postedToLedgerCount}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text">
                      Tổng Nợ: {formatMoney(summary.totalDebit || 0)} | Tổng Có: {formatMoney(summary.totalCredit || 0)}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
                <SoftBox mt="auto" pt={2} display="flex" justifyContent="flex-end" gap={1}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigate(baseRoute)}>
                    Quay lại
                  </SoftButton>
                  <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                    In Bảng
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
